"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import JobApplicationDialog from "@/components/careers/JobApplicationDialog";
import { JOB_DESCRIPTION_COMPONENTS } from "@/components/careers/jobDescriptionComponents";
import RuntimeMdx from "@/components/common/RuntimeMdx";
import ShareButtons from "@/components/common/ShareButtons";
import Wrapper from "@/components/common/Wrapper";
import Button from "@/components/library/Button";
import { BASE_URL } from "@/utils/baseUrl";
import { jobDetailPath, jobTypeLabel, type JobListing } from "@/utils/jobsPublic";
import type { SupportedLanguage } from "@/utils/library/languages";
import { getTranslatedPath } from "@/utils/routes";

type JobDetailsContentProps = {
  job: JobListing;
  lang: SupportedLanguage;
};

function LocationIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path d="M12 21s7-4.35 7-11a7 7 0 1 0-14 0c0 6.65 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

export default function JobDetailsContent({
  job,
  lang,
}: Readonly<JobDetailsContentProps>) {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [applicationOpen, setApplicationOpen] = useState(
    () => searchParams.get("apply") != null && job.applyEnabled !== false,
  );

  const careersHref = `/${lang}${getTranslatedPath("/careers", lang)}`;
  const shareUrl = `${BASE_URL}${jobDetailPath(job, lang)}`;
  const sourceUrl = useMemo(() => {
    if (globalThis.window !== undefined) return globalThis.window.location.href;
    return shareUrl;
  }, [shareUrl]);

  const clearApplyParam = () => {
    if (searchParams.get("apply") == null) return;
    const params = new URLSearchParams(searchParams.toString());
    params.delete("apply");
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
  };

  const openApplication = () => setApplicationOpen(true);

  const handleApplicationOpenChange = (nextOpen: boolean) => {
    setApplicationOpen(nextOpen);
    if (!nextOpen) clearApplyParam();
  };

  return (
    <>
      <Wrapper fullWidth className="py-10 md:py-14 w-full max-w-5xl px-4!">
        <Link
          href={careersHref}
          className="inline-flex items-center gap-2 text-sm font-medium transition-colors font-monument text-black/60 hover:text-black"
        >
          <BackIcon />
          {t("careers.backToAllRoles")}
        </Link>

        <div className="mt-6 overflow-hidden rounded-3xl border border-black/10">
          <div className="px-6 py-8 sm:px-10 sm:py-12 border-b bg-marketing-voice-card border-black/10">
            <span className="inline-flex w-fit rounded-full px-3 py-1 font-monument font-bold text-xs tracking-wide uppercase bg-gail-primary-light text-gail-primary">
              {job.departmentLabel ?? jobTypeLabel(job.type)}
            </span>
            <h1 className="mt-4 sm:mt-5 max-w-4xl font-monument text-foreground leading-tight tracking-tight font-normal text-3xl sm:text-4xl md:text-5xl">
              {job.title}
            </h1>
            <div className="mt-4 sm:mt-6 flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-black/60">
              {job.location ? (
                <span className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-white px-3 py-1.5 sm:px-4 sm:py-2 border border-black/10">
                  <LocationIcon />
                  {job.location}
                </span>
              ) : null}
              {job.compensation ? (
                <span className="inline-flex rounded-full bg-white px-3 py-1.5 sm:px-4 sm:py-2 border border-black/10">
                  {job.compensation}
                </span>
              ) : null}
              <Button
                type="primary"
                onClick={openApplication}
                disabled={job.applyEnabled === false}
                className="inline-flex! h-10 items-center justify-center px-6! py-0! sm:ml-1"
                textClassName="leading-none"
              >
                {t("careers.applyNow")}
              </Button>
            </div>
          </div>

          <div className="bg-white px-6 py-8 sm:px-10 sm:py-12">
            <section className="mb-8">
              <h2 className="font-monument font-medium text-foreground text-2xl">
                {t("careers.aboutRole")}
              </h2>
              <p className="mt-3 text-lg leading-relaxed text-black/60">
                {job.summary}
              </p>
            </section>

            <div className="max-w-none">
              <RuntimeMdx
                source={job.description}
                components={JOB_DESCRIPTION_COMPONENTS}
              />
            </div>

            <div className="mt-10 flex flex-col gap-4 border-t pt-8 sm:flex-row sm:items-center sm:justify-between border-black/10">
              <div className="flex flex-col items-start gap-3">
                <div className="flex items-center gap-2 rounded-full border px-3 py-1.5 sm:px-4 sm:py-2 border-black/10">
                  <span className="text-xs sm:text-sm text-black/50">
                    {t("careers.share")}
                  </span>
                  <ShareButtons
                    url={shareUrl}
                    title={job.title}
                    size={22}
                    variant="dark"
                    utmContent={job.id}
                  />
                </div>
                <p className="text-sm text-black/50">
                  {t("careers.questions")}{" "}
                  <a
                    href="mailto:careers@meetgail.com"
                    className="font-medium text-foreground hover:text-gail-primary"
                  >
                    careers@meetgail.com
                  </a>
                </p>
              </div>
              <Button
                type="primary"
                onClick={openApplication}
                disabled={job.applyEnabled === false}
                className="inline-flex! h-11 w-full items-center justify-center px-8! py-0! sm:w-auto"
                textClassName="leading-none"
              >
                {t("careers.applyNow")}
              </Button>
            </div>
          </div>
        </div>
      </Wrapper>

      <JobApplicationDialog
        job={job}
        open={applicationOpen}
        onOpenChange={handleApplicationOpenChange}
        locale={lang}
        sourceUrl={sourceUrl}
      />
    </>
  );
}
